import React, {useState} from 'react';
import {motion} from "framer-motion";
import {useTranslation} from "react-i18next";
import {StepLeaderboard} from "@/features/contest/StepLeaderboard.jsx";
import {HalfTimeRewardBanner} from "@/features/contest/HalfTimeRewardBanner.jsx";

const PHASE = {HALF_TIME: 'HALF_TIME', FINAL: 'FINAL'};

const Tab = ({active, onClick, icon, label, live, frozen}) => (
    <button
        onClick={onClick}
        className={`flex-1 relative flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-black transition-all ${active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
    >
        <span>{icon}</span>
        <span className="truncate">{label}</span>
        {live && <span className="w-1.5 h-1.5 rounded-full bg-action-green animate-pulse"/>}
        {frozen && <span className="text-[10px] text-gray-400">✓</span>}
    </button>
);

// Classement à onglets Mi-temps / Final.
// - Un SEUL classement à l'écran à la fois (jamais deux surfaces) ; on bascule par onglet.
// - L'onglet de la phase EN COURS (`currentPhase`) est "live" (pastille qui pulse) et sélectionné
//   par défaut ; il se re-focalise tout seul quand la phase change (sifflet mi-temps).
// - 1ère période : Mi-temps = live, Final = provisoire (« se jouera en 2e période »).
// - Après la pause : Mi-temps = FIGÉ (badge ✓ + gagnant), Final = live.
export const LeaderboardTabs = ({
                                    finalLeaderboard,
                                    halfTimeLeaderboard = null,
                                    currentPhase = PHASE.FINAL,
                                    played = false,
                                    halfTimeReward = null,
                                    onShowHalfTimeReward = () => {},
                                    canPlayHalfTime = false,
                                    onPlayHalfTime = () => {},
                                    canPlayQuiz = false,
                                    onPlayQuiz = () => {},
                                    isGoalFlashing = false,
                                    setSelectedPlayer = () => {},
                                }) => {
    const {t} = useTranslation();

    // L'onglet sélectionné suit automatiquement la phase en cours, tout en restant
    // manuellement changeable. Pattern "reset state on prop change" sans effet.
    const [phase, setPhase] = useState(currentPhase);
    const [prevPhase, setPrevPhase] = useState(currentPhase);
    if (currentPhase !== prevPhase) {
        setPrevPhase(currentPhase);
        setPhase(currentPhase);
    }

    // Pas de lot mi-temps sur ce concours → comportement historique (un seul classement, pas d'onglets).
    if (!halfTimeLeaderboard) {
        return (
            <StepLeaderboard
                leaderboard={finalLeaderboard}
                isGoalFlashing={isGoalFlashing}
                setSelectedPlayer={setSelectedPlayer}
                halfTimeReward={halfTimeReward}
                onShowHalfTimeReward={onShowHalfTimeReward}
            />
        );
    }

    const showHalfTime = phase === PHASE.HALF_TIME;
    const htIsLive = currentPhase === PHASE.HALF_TIME; // 1ère période : la mi-temps est la phase live

    return (
        <div className="mt-4">
            {/* Lot mi-temps gagné : AU-DESSUS des onglets (rendu null si aucun lot). */}
            <HalfTimeRewardBanner halfTimeReward={halfTimeReward} onShow={onShowHalfTimeReward} className="mb-3"/>

            {/* Encarts d'incitation, juste sous le lot : jouer la mi-temps / le quiz. */}
            {canPlayHalfTime && (
                <motion.button
                    initial={{opacity: 0, y: -10}}
                    animate={{opacity: 1, y: 0}}
                    onClick={onPlayHalfTime}
                    className="w-full mb-3 flex items-center justify-between gap-3 bg-brand-dark text-white rounded-2xl px-4 py-3 shadow-[0_8px_24px_-8px_rgba(99,102,241,0.5)] active:scale-[0.98] transition-transform"
                >
                    <span className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-base shrink-0">⏱️</span>
                        <span className="text-left">
                            <span className="block text-sm font-black leading-tight">
                                {t('leaderboard_tabs.play_ht_title', 'Joue la mi-temps')}
                            </span>
                            <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                {t('leaderboard_tabs.play_ht_sub', 'Tente un 2e lot avant la pause')}
                            </span>
                        </span>
                    </span>
                    <span className="text-xs font-black text-indigo-300 uppercase tracking-widest shrink-0">
                        {t('leaderboard_tabs.play_ht_cta', 'Jouer')} →
                    </span>
                </motion.button>
            )}

            {canPlayQuiz && (
                <motion.button
                    initial={{opacity: 0, y: -10}}
                    animate={{opacity: 1, y: 0}}
                    onClick={onPlayQuiz}
                    className="w-full mb-3 flex items-center justify-between gap-3 bg-brand-dark text-white rounded-2xl px-4 py-3 shadow-[0_8px_24px_-8px_rgba(16,185,129,0.5)] active:scale-[0.98] transition-transform"
                >
                    <span className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-full bg-action-green/20 border border-action-green/30 flex items-center justify-center text-base shrink-0">⚡</span>
                        <span className="text-left">
                            <span className="block text-sm font-black leading-tight">
                                {t('leaderboard.quiz_boost.banner_title', 'Booste ton score')}
                            </span>
                            <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                {t('leaderboard.quiz_boost.banner_sub', 'Joue le quiz du jour')}
                            </span>
                        </span>
                    </span>
                    <span className="text-xs font-black text-action-green uppercase tracking-widest shrink-0">
                        {t('leaderboard.quiz_boost.banner_cta', 'Jouer')} →
                    </span>
                </motion.button>
            )}

            {/* Barre d'onglets */}
            <div className="flex gap-1 p-1 bg-slate-100 rounded-2xl mb-2">
                <Tab
                    active={showHalfTime}
                    onClick={() => setPhase(PHASE.HALF_TIME)}
                    icon="⏱️"
                    label={t('leaderboard_tabs.half_time', 'Mi-temps')}
                    live={htIsLive}
                    frozen={!htIsLive}
                />
                <Tab
                    active={!showHalfTime}
                    onClick={() => setPhase(PHASE.FINAL)}
                    icon="🏁"
                    label={t('leaderboard_tabs.final', 'Final')}
                    live={!htIsLive}
                />
            </div>

            {showHalfTime ? (
                <motion.div
                    key="ht"
                    initial={{opacity: 0, x: -12}}
                    animate={{opacity: 1, x: 0}}
                >

                    {!played && (
                        <p className="text-center text-[11px] text-gray-400 italic font-medium mb-2 px-6">
                            {t('leaderboard_tabs.not_played', "Tu n'as pas joué la mi-temps cette fois.")}
                        </p>
                    )}

                    <StepLeaderboard
                        leaderboard={halfTimeLeaderboard}
                        isGoalFlashing={false}
                        setSelectedPlayer={setSelectedPlayer}
                    />
                </motion.div>
            ) : (
                <motion.div
                    key="final"
                    initial={{opacity: 0, x: 12}}
                    animate={{opacity: 1, x: 0}}
                >

                    <StepLeaderboard
                        leaderboard={finalLeaderboard}
                        isGoalFlashing={isGoalFlashing}
                        setSelectedPlayer={setSelectedPlayer}
                    />
                </motion.div>
            )}
        </div>
    );
};

export default LeaderboardTabs;
